import { createSlice } from '@reduxjs/toolkit';
import { batch } from 'react-redux';
import axios from 'src/utils/axios';

const defaultFilterValues = {
  numberOfCheckIns: {
    comparator: '=',
    value: null
  },
  differentLocations: {
    comparator: '=',
    value: null
  },
  numberOfPoints: {
    comparator: '=',
    value: null
  },
  showNonMembers: false,
  showMembers: false,
  showCompanyTeams: false,
  showFriendTeams: false,
  showFamilyTeams: false,
  showSinglePerson: false,
  isFilterActive: false
};

const initialState = {
  groups: [],
  groupTypes: [],
  selectedGroup: null,
  filterValues: { ...defaultFilterValues },
  isFetching: false
};

const sumValue = (group, groups, key = 'points') => {
  let sum = group[key];

  groups.forEach(child => {
    if (child.parentGroupId === group.id) {
      sum += sumValue(child, groups, key);
    }
  });

  return sum;
};

const slice = createSlice({
  name: 'group',
  initialState,
  reducers: {
    deleteGroup(state, action) {
      const { group } = action.payload;

      const groups = state.groups.filter(item => item.id !== group.id);

      state.groups = groups;
    },
    getGroupTypes(state, action) {
      const { groupTypes } = action.payload;

      state.groupTypes = groupTypes;
    },
    getGroups(state, action) {
      const { groupTypes } = action.payload;

      const groups = [];

      groupTypes.forEach(type => {
        type.groups.forEach(group => {
          groups.push({
            id: group.id,
            parentOnly: group.parentOnly,
            parentGroupId: group.parentGroupId,
            groupType: type,
            groupTypeId: group.groupTypeId,
            name: group.name,
            rank: group.ranking?.ranking || '-',
            type: type.name,
            participants: group.participations.length,
            trips: group.ranking?.totalCheckIns || 0,
            uniqueTrips: group.ranking?.totalUniqueLocations || 0,
            points: group.ranking?.totalPoints || 0,
            averagePoints:
              group.participations.length > 0
                ? group.ranking?.totalPoints / group.participations.length
                : 0
          });
        });
      });

      groups.forEach(group => {
        group.participants = sumValue(group, groups, 'participants');
        group.trips = sumValue(group, groups, 'trips');
        group.points = sumValue(group, groups, 'points');
        group.averagePoints = group.points / (group.participants || 1);
      });

      groups.sort((a, b) => {
        if (a.averagePoints < b.averagePoints) {
          return 1;
        }

        if (a.averagePoints > b.averagePoints) {
          return -1;
        }

        return 0;
      });

      state.groups = groups;
      state.groupTypes = groupTypes;
    },
    setGroups(state, action) {
      const { groups } = action.payload;

      state.groups = groups;
    },
    setSelectedGroup(state, action) {
      const { group } = action.payload;

      state.selectedGroup = group;
    },
    setFilterValues(state, action) {
      const { filterValues } = action.payload;
      const updated = { ...state.filterValues, ...filterValues };

      state.filterValues = updated;
    },
    setFetching(state, action) {
      const { isFetching } = action.payload;

      state.isFetching = isFetching;
    }
  }
});

export const { reducer } = slice;

export const getGroups = contestId => async dispatch => {
  const query = `
    {
      contest{
        groupTypes{
          id,
          name,
          minParticipants,
          maxParticipants,
          groups{
            id,
            name,
            groupTypeId,
            ranking {
              totalPoints,
              totalCheckIns,
              totalLength,
              totalActivities,
              totalUniqueLocations,
              ranking
            },
            parentOnly,
            parentGroupId,
            participations{
              id
            }
          }
        }
      }
    }
  `;

  dispatch(slice.actions.setFetching({ isFetching: true }));

  const response = await axios.post(`/admin/${contestId}/g`, { query });

  batch(() => {
    dispatch(slice.actions.setFetching({ isFetching: false }));
    dispatch(slice.actions.getGroups(response.data.data.contest));
  });
};

export const setSelectedGroup = group => async dispatch => {
  dispatch(slice.actions.setSelectedGroup({ group }));
};

export const deleteGroup = (group, contestId) => async dispatch => {
  try {
    const response = await axios.delete(
      `/admin/${contestId}/groups/${group.id}`
    );

    if (response.status !== 200) {
      return response.data.message;
    }

    dispatch(slice.actions.deleteGroup({ group }));

    return true;
  } catch (e) {
    return e.message;
  }
};

export const setFilterValues = filterValues => async dispatch => {
  dispatch(slice.actions.setFilterValues({ filterValues }));
};

export const resetFilterValues = () => async dispatch => {
  dispatch(
    slice.actions.setFilterValues({ filterValues: defaultFilterValues })
  );
};

export const clearGroups = () => async dispatch => {
  dispatch(slice.actions.setGroups({ groups: [] }));
};

export default slice;
